import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { dedupeIds, readStoredIdList } from "@/lib/storage";

const STORAGE_KEY = "worksnavi:favorites";

interface FavoritesContextValue {
  favorites: string[];
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
  removeFavorite: (id: string) => void;
  /** 複数IDをまとめて外す（表示できない作品の一括解除など） */
  removeMany: (ids: string[]) => void;
  /** バックアップのインポート用（キーは変更しない） */
  applyImported: (ids: string[], strategy: "merge" | "replace") => void;
  /** インポート時にストレージへ書き込んだ内容を状態へ反映する */
  hydrate: (ids: string[]) => void;
  count: number;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

function readStored(): string[] {
  // 壊れた値・重複IDは読み込み時に取り除く（保存キーは変更しない）
  return dedupeIds(readStoredIdList(STORAGE_KEY));
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [favorites, setFavorites] = useState<string[]>(readStored);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch {
      /* 保存できない環境ではセッション内のみ反映 */
    }
  }, [favorites]);

  const toggleFavorite = useCallback((id: string) => {
    const value = id.trim();
    if (!value) return;
    // 大文字・小文字が違うIDでも同じ作品として扱う（二重登録を防ぐ）
    const key = value.toLowerCase();
    setFavorites((current) =>
      current.some((item) => item.toLowerCase() === key)
        ? current.filter((item) => item.toLowerCase() !== key)
        : [value, ...current],
    );
  }, []);

  const removeFavorite = useCallback((id: string) => {
    const key = id.trim().toLowerCase();
    setFavorites((current) => current.filter((item) => item.toLowerCase() !== key));
  }, []);

  const removeMany = useCallback((ids: string[]) => {
    const targets = new Set(ids.map((id) => id.toLowerCase()));
    setFavorites((current) => current.filter((item) => !targets.has(item.toLowerCase())));
  }, []);

  const applyImported = useCallback((ids: string[], strategy: "merge" | "replace") => {
    const incoming = dedupeIds(ids);
    setFavorites((current) => {
      if (strategy === "replace") return incoming;
      return dedupeIds([...current, ...incoming]);
    });
  }, []);

  const hydrate = useCallback((ids: string[]) => {
    setFavorites(dedupeIds(ids));
  }, []);

  const value = useMemo<FavoritesContextValue>(
    () => ({
      favorites,
      isFavorite: (id: string) =>
        favorites.some((item) => item.toLowerCase() === id.trim().toLowerCase()),
      toggleFavorite,
      removeFavorite,
      removeMany,
      applyImported,
      hydrate,
      count: favorites.length,
    }),
    [favorites, toggleFavorite, removeFavorite, removeMany, applyImported, hydrate],
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites(): FavoritesContextValue {
  const context = useContext(FavoritesContext);
  if (!context) throw new Error("useFavorites must be used within FavoritesProvider");
  return context;
}